/**
 * DapperFam Horizontal Filter Bar - "Modern Ranch House"
 * JavaScript for dropdown interactions and filter submissions
 */

(function() {
  'use strict';

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initFilterBar();
      observeGridContainer();
    });
  } else {
    initFilterBar();
    observeGridContainer();
  }

  function initFilterBar() {
    const filterBar = document.getElementById('FilterBarHorizontal');
    if (!filterBar) return;

    // Mark as initialized so MutationObserver doesn't double-init
    filterBar.dataset.initialized = 'true';

    // Initialize dropdown toggles
    initDropdowns(filterBar);

    // Initialize filter form submission
    initFilterSubmission(filterBar);

    // Initialize sticky behavior
    initStickyBehavior(filterBar);

    // Initialize keyboard navigation
    initKeyboardNavigation(filterBar);
  }

  /**
   * Re-initialize filter bar when ProductGridContainer is replaced by AJAX filtering.
   * FacetFiltersForm.renderPage() replaces the entire container innerHTML,
   * destroying all event listeners on the old filter bar DOM nodes.
   */
  function observeGridContainer() {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;

    const observer = new MutationObserver(function() {
      const newFilterBar = document.getElementById('FilterBarHorizontal');
      if (newFilterBar && !newFilterBar.dataset.initialized) {
        initFilterBar();
      }
    });

    observer.observe(gridContainer, { childList: true, subtree: false });
  }

  /**
   * Initialize dropdown toggle functionality
   */
  function initDropdowns(filterBar) {
    const triggers = filterBar.querySelectorAll('[data-filter-trigger]');

    triggers.forEach(trigger => {
      trigger.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();

        const dropdown = this.closest('.filter-dropdown');
        const isOpen = dropdown.classList.contains('is-open');

        // Close all other dropdowns
        closeAllDropdowns(filterBar);

        // Toggle current dropdown
        if (!isOpen) {
          openDropdown(dropdown);
        }
      });
    });

    // Close dropdowns when clicking outside
    document.addEventListener('click', function(e) {
      if (!e.target.closest('.filter-dropdown')) {
        closeAllDropdowns(filterBar);
      }
    });

    // Close on escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        closeAllDropdowns(filterBar);
      }
    });
  }

  /**
   * Open a dropdown
   */
  function openDropdown(dropdown) {
    dropdown.classList.add('is-open');
    const trigger = dropdown.querySelector('[data-filter-trigger]');
    const panel = dropdown.querySelector('.filter-dropdown-panel');

    if (trigger) {
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Focus first input in the panel
    if (panel) {
      const firstInput = panel.querySelector('input:not([disabled])');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 100);
      }
    }
  }

  /**
   * Close all dropdowns
   */
  function closeAllDropdowns(filterBar) {
    const dropdowns = filterBar.querySelectorAll('.filter-dropdown');

    dropdowns.forEach(dropdown => {
      dropdown.classList.remove('is-open');
      const trigger = dropdown.querySelector('[data-filter-trigger]');
      if (trigger) {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  /**
   * Initialize filter form submission
   */
  function initFilterSubmission(filterBar) {
    const filterInputs = filterBar.querySelectorAll('[data-filter-input]');
    const priceInputs = filterBar.querySelectorAll('[data-price-min], [data-price-max]');

    // Checkbox and radio inputs - submit on change
    filterInputs.forEach(input => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.addEventListener('change', function() {
          submitFilters(filterBar);
        });
      }
    });

    // Price inputs - debounce and submit
    let priceDebounceTimer;
    priceInputs.forEach(input => {
      input.addEventListener('input', function() {
        clearTimeout(priceDebounceTimer);
        priceDebounceTimer = setTimeout(() => {
          submitFilters(filterBar);
        }, 800);
      });

      input.addEventListener('keypress', function(e) {
        if (e.key === 'Enter') {
          clearTimeout(priceDebounceTimer);
          submitFilters(filterBar);
        }
      });
    });
  }

  /**
   * Submit filters by constructing URL and navigating
   */
  function submitFilters(filterBar) {
    const searchParams = new URLSearchParams(window.location.search);

    // Keep sort_by if present
    const sortBy = searchParams.get('sort_by');

    // Build new params from checked inputs
    const newParams = new URLSearchParams();

    if (sortBy) {
      newParams.set('sort_by', sortBy);
    }

    // Get all filter dropdowns
    const dropdowns = filterBar.querySelectorAll('.filter-dropdown:not(.sort-dropdown)');

    dropdowns.forEach(dropdown => {
      const checkedInputs = dropdown.querySelectorAll('input:checked');
      checkedInputs.forEach(input => {
        if (input.name && input.value) {
          newParams.append(input.name, input.value);
        }
      });

      // Handle price range inputs
      const minInput = dropdown.querySelector('[data-price-min]');
      const maxInput = dropdown.querySelector('[data-price-max]');

      if (minInput && minInput.value) {
        newParams.set(minInput.name, minInput.value);
      }

      if (maxInput && maxInput.value) {
        newParams.set(maxInput.name, maxInput.value);
      }
    });

    // Also check the mobile/sidebar form if it exists (for sync)
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const mobileInputs = mobileForm.querySelectorAll('input:checked');
      mobileInputs.forEach(input => {
        if (input.name && input.value) {
          // Only add if not already in horizontal bar
          const existsInHorizontal = filterBar.querySelector(`input[name="${input.name}"][value="${input.value}"]`);
          if (!existsInHorizontal) {
            newParams.append(input.name, input.value);
          }
        }
      });
    }

    // Build search params string (without leading ?)
    const paramsString = newParams.toString();

    // Show loading state
    showLoadingState();

    // Use the FacetFiltersForm.renderPage if available (AJAX filtering)
    if (typeof FacetFiltersForm !== 'undefined' && typeof FacetFiltersForm.renderPage === 'function') {
      FacetFiltersForm.renderPage(paramsString, null, true);
    } else {
      // Fallback to full page navigation
      const newUrl = `${window.location.pathname}${paramsString ? '?' + paramsString : ''}`;
      window.location.href = newUrl;
    }
  }

  /**
   * Show loading state on filter bar
   */
  function showLoadingState() {
    const productCount = document.getElementById('ProductCountHorizontal');
    if (productCount) {
      productCount.innerHTML = '<span class="loading-overlay">Loading...</span>';
    }
  }

  /**
   * Initialize sticky behavior for filter bar
   * Only becomes sticky after scrolling past the original filter bar position
   * NOTE: When the CollectionNavBar exists, sticky is handled by the nav bar JS instead.
   */
  function initStickyBehavior(filterBar) {
    // If the combined nav bar exists, it handles stickiness — skip
    if (document.getElementById('CollectionNavBar')) return;

    let filterBarTop = 0;
    let filterBarHeight = 0;
    let ticking = false;
    let placeholder = null;

    // Create placeholder element to prevent layout shift
    function createPlaceholder() {
      if (!placeholder) {
        placeholder = document.createElement('div');
        placeholder.className = 'filter-bar-placeholder';
        placeholder.style.display = 'none';
        placeholder.style.width = '100%';
        filterBar.parentNode.insertBefore(placeholder, filterBar.nextSibling);
      }
    }

    // Calculate the filter bar's original position
    function calculatePosition() {
      // Temporarily remove sticky class to get true position
      const wasSticky = filterBar.classList.contains('is-sticky');
      if (wasSticky) {
        filterBar.classList.remove('is-sticky');
        if (placeholder) placeholder.style.display = 'none';
      }

      const rect = filterBar.getBoundingClientRect();
      filterBarTop = rect.top + window.scrollY;
      filterBarHeight = rect.height;

      // Update placeholder height
      if (placeholder) {
        placeholder.style.height = filterBarHeight + 'px';
      }

      if (wasSticky) {
        filterBar.classList.add('is-sticky');
        if (placeholder) placeholder.style.display = 'block';
      }
    }

    // Create placeholder
    createPlaceholder();

    // Initial calculation
    calculatePosition();

    // Recalculate on resize
    window.addEventListener('resize', debounce(calculatePosition, 100));

    function updateSticky() {
      // Account for the sticky header height so the bar pins below it
      var headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 55;
      if (window.scrollY >= filterBarTop - headerHeight) {
        if (!filterBar.classList.contains('is-sticky')) {
          filterBar.classList.add('is-sticky');
          if (placeholder) placeholder.style.display = 'block';
        }
      } else {
        if (filterBar.classList.contains('is-sticky')) {
          filterBar.classList.remove('is-sticky');
          if (placeholder) placeholder.style.display = 'none';
        }
      }
      ticking = false;
    }

    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(updateSticky);
        ticking = true;
      }
    }, { passive: true });
  }

  // Simple debounce helper
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Initialize keyboard navigation
   */
  function initKeyboardNavigation(filterBar) {
    const dropdowns = filterBar.querySelectorAll('.filter-dropdown');

    dropdowns.forEach(dropdown => {
      const trigger = dropdown.querySelector('[data-filter-trigger]');
      const panel = dropdown.querySelector('.filter-dropdown-panel');

      if (!trigger || !panel) return;

      // Arrow key navigation in panel
      panel.addEventListener('keydown', function(e) {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();

          const focusableElements = panel.querySelectorAll(
            'input:not([disabled]), a:not([disabled]), button:not([disabled])'
          );
          const currentIndex = Array.from(focusableElements).indexOf(document.activeElement);

          let nextIndex;
          if (e.key === 'ArrowDown') {
            nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
          } else {
            nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
          }

          focusableElements[nextIndex]?.focus();
        }

        // Tab trap within dropdown
        if (e.key === 'Tab') {
          const focusableElements = panel.querySelectorAll(
            'input:not([disabled]), a:not([disabled]), button:not([disabled])'
          );
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      });
    });
  }

  /**
   * Sync horizontal filters with sidebar (if both exist)
   */
  function syncWithSidebar() {
    const horizontalBar = document.getElementById('FilterBarHorizontal');
    const sidebarForm = document.getElementById('FacetFiltersForm');

    if (!horizontalBar || !sidebarForm) return;

    // When sidebar changes, update horizontal bar
    sidebarForm.addEventListener('change', function(e) {
      const input = e.target;
      if (!input.name) return;

      const horizontalInput = horizontalBar.querySelector(
        `input[name="${input.name}"][value="${input.value}"]`
      );

      if (horizontalInput) {
        horizontalInput.checked = input.checked;
        updateDropdownBadge(horizontalInput.closest('.filter-dropdown'));
      }
    });
  }

  /**
   * Update the count badge on a dropdown
   */
  function updateDropdownBadge(dropdown) {
    if (!dropdown) return;

    const trigger = dropdown.querySelector('[data-filter-trigger]');
    const checkedCount = dropdown.querySelectorAll('input:checked').length;
    let badge = trigger.querySelector('.filter-count-badge');

    if (checkedCount > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'filter-count-badge';
        trigger.insertBefore(badge, trigger.querySelector('svg'));
      }
      badge.textContent = checkedCount;
      trigger.classList.add('is-active');
    } else {
      if (badge) {
        badge.remove();
      }
      trigger.classList.remove('is-active');
    }
  }

  // Initialize sync
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncWithSidebar);
  } else {
    syncWithSidebar();
  }

  // Expose for external use
  window.DapperFamFilterBar = {
    closeAllDropdowns: function() {
      const filterBar = document.getElementById('FilterBarHorizontal');
      if (filterBar) closeAllDropdowns(filterBar);
    },
    submitFilters: function() {
      const filterBar = document.getElementById('FilterBarHorizontal');
      if (filterBar) submitFilters(filterBar);
    }
  };

})();
